import React, {useMemo, useCallback, useState} from 'react';
import _ from 'lodash';
import {useQuery, useQueryClient} from '@tanstack/react-query';
import {useSnackbar} from 'notistack';
import {Autocomplete, Box, Card, Container, IconButton, Skeleton, Stack, Table,
    TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip,
    Typography} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import NoteIcon from '@mui/icons-material/Note';
import AddIcon from '@mui/icons-material/Add';
import withMainContainer from '../components/WithMainContainer.js';
import {useLoaderState} from '../components/Loader.js';
import SCButton from '../components/SCButton.js';
import {formatTime} from '../util/util.js';
import api from '../util/api.js';
import {closeModal, openModal} from '../components/Modals.js';
import SCTextField from '../components/SCTextField.js';
import {useBusyCallback} from '../util/react_hooks.js';
import SCNumberField from '../components/SCNumberField.js';
import SCTimePicker from '../components/SCTimePicker.js';

const usePurchases = ()=>{
    let {data, isLoading} = useQuery({
        queryKey: ['purchases'],
        queryFn: ()=>api.admin.purchases.get(),
    });
    return [data?.purchases, isLoading];
};

const useAllUsers = ()=>{
    let {data, isLoading} = useQuery({
        queryKey: ['adminUsers'],
        queryFn: ()=>api.admin.users(),
    });
    return [data?.users||[], isLoading];
};

const ManualPurchaseModal = props=>{
    let {
        id,
        description: _description,
        amount: _amount,
        completed: _completed,
        userId: _userId,
        note: _note,
    } = props;
    let [description, setDescription] = useState(_description||'');
    let [amount, setAmount] = useState(_amount||0);
    let [userId, setUserId] = useState(_userId||'');
    let [completed, setCompleted] = useState(
        _completed ? new Date(_completed) : new Date());
    let [note, setNote] = useState(_note||'');
    let {enqueueSnackbar} = useSnackbar();
    let queryClient = useQueryClient();
    let [confirm, confirming] = useBusyCallback(async ()=>{
        try {
            if (id)
            {
                await api.admin.purchases.update(id,
                    {description, amount, completed, userId, note});
                enqueueSnackbar('הרכישה עודכנה בהצלחה', {variant: 'success'});
            }
            else
            {
                await api.admin.purchases.create(
                    {description, amount, completed, userId, note});
                enqueueSnackbar('הרכישה נוספה בהצלחה', {variant: 'success'});
            }
            closeModal();
            queryClient.invalidateQueries({queryKey: ['purchases']});
        } catch (e) {
            console.error(e);
            enqueueSnackbar('אירעה שגיאה', {variant: 'error'});
        }
    }, [id, description, amount, completed, userId, note,
        enqueueSnackbar, queryClient]);
    let [_users, usersLoading] = useAllUsers();
    let users = useMemo(()=>_users.filter(
        u=>!u.permissions?.includes('admin')), [_users]);
    let usersById = useMemo(()=>_.keyBy(users, 'id'), [users]);
    let options = useMemo(()=>_.map(users, 'id'), [users]);
    let getOptionLabel = useCallback(id=>{
        let user = usersById[id];
        if (!id?.length)
            return '';
        return `${user.username} (${user.email})`;
    }, [usersById]);
    let [userInput, setUserInput] = useState('');
    let disabled = !description?.length||!userId?.length;
    return <Stack direction="column" gap={2}>
      <SCTextField required label="תיאור" value={description} onChange={setDescription}/>
      <SCNumberField required label="סכום" min={0} value={amount} onChange={setAmount}/>
      {usersLoading ? <Skeleton
        variant="rounded"
        width={100}
        height={40}
      /> : <Autocomplete
        value={userId}
        onChange={(__, v)=>setUserId(v)}
        options={options}
        getOptionLabel={getOptionLabel}
        inputValue={userInput}
        onInputChange={(__, v)=>setUserInput(v)}
        renderInput={p=><SCTextField label="משתמש" required {...p}/>}
      />}
      <Stack direction="row" gap={2} flexWrap="wrap">
        <Stack direction="row" alignItems="center">
          <Box width="100px">
            <SCNumberField
              label="שנה"
              min={0}
              max={3000}
              width="100px"
              size="small"
              value={completed.getFullYear()}
              onChange={v=>setCompleted(p=>{
                  let n = new Date(p);
                  n.setFullYear(v);
                  return n;
              })}
            />
          </Box>
          <Box width="100px">
            <SCNumberField
              label="חודש"
              min={1}
              max={12}
              width="100px"
              size="small"
              value={completed.getMonth()+1}
              onChange={v=>setCompleted(p=>{
                  let n = new Date(p);
                  n.setMonth(v-1);
                  return n;
              })}
            />
          </Box>
          <Box width="100px">
            <SCNumberField
              label="יום"
              min={1}
              max={31}
              width="100px"
              size="small"
              value={completed.getDate()}
              onChange={v=>setCompleted(p=>{
                  let n = new Date(p);
                  n.setDate(v);
                  return n;
              })}
            />
          </Box>
        </Stack>
        <SCTimePicker label="שעה" value={completed} onChange={setCompleted}/>
      </Stack>
      <SCTextField label="הערות" value={note} onChange={setNote}/>
      <SCButton variant="contained" text="שמירה" onClick={confirm} loading={confirming}
        disabled={disabled}/>
    </Stack>;
};

const NoteModal = props=>{
    let {id, note: _note} = props;
    let [note, setNote] = useState(_note||'');
    let {enqueueSnackbar} = useSnackbar();
    let queryClient = useQueryClient();
    let [confirm, confirming] = useBusyCallback(async ()=>{
        try {
            await api.admin.purchases.update(id, {note});
            enqueueSnackbar('ההערות עודכנו בהצלחה', {variant: 'success'});
            closeModal();
            queryClient.invalidateQueries({queryKey: ['purchases']});
        } catch (e) {
            console.error(e);
            enqueueSnackbar('אירעה שגיאה', {variant: 'error'});
        }
    }, [id, note, enqueueSnackbar, queryClient]);
    return <Stack direction="column" gap={2}>
      <SCTextField label="הערות" value={note} onChange={setNote}/>
      <SCButton variant="contained" text="שמירה" onClick={confirm} loading={confirming}/>
    </Stack>;
};

const DeleteModal = props=>{
    let {id} = props;
    let {enqueueSnackbar} = useSnackbar();
    let queryClient = useQueryClient();
    let [confirm, confirming] = useBusyCallback(async ()=>{
        try {
            await api.admin.purchases.remove(id);
            enqueueSnackbar('הרכישה נמחקה בהצלחה', {variant: 'success'});
            closeModal();
            queryClient.invalidateQueries({queryKey: ['purchases']});
        } catch (e) {
            console.error(e);
            enqueueSnackbar('אירעה שגיאה', {variant: 'error'});
        }
    }, [id, enqueueSnackbar, queryClient]);
    return <Stack direction="row" gap={2} alignItems="center"
      justifyContent="end">
      <SCButton color="error" variant="contained"
        text="מחיקה" icon={<DeleteIcon/>}
        onClick={confirm} loading={confirming}/>
    </Stack>
};

const AdminPurchases = ()=>{
    let [purchases, loading] = usePurchases();
    useLoaderState(loading);
    let total = useMemo(()=>purchases?.reduce((acc, v)=>acc+v.amount, 0),
        [purchases]);
    let addPurchase = useCallback(()=>openModal({
        size: 'lg',
        header: 'הוספת רכישה באופן ידני',
        content: <ManualPurchaseModal/>,
    }), []);
    let deletePurchase = useCallback(p=>openModal({
        header: 'האם למחוק את הרכישה?',
        content: <DeleteModal {...p}/>,
    }), []);
    let updatePurchase = useCallback(p=>openModal({
        size: 'lg',
        header: 'עריכת רכישה',
        content: <ManualPurchaseModal {...p}/>,
    }), []);
    let editNote = useCallback(p=>openModal({
        size: 'lg',
        header: 'עריכת הערות',
        content: <NoteModal {...p}/>,
    }), []);
    return <Container maxWidth="lg" sx={{marginTop: 4, marginBottom: 12}}>
      <Stack direction="column" gap={2}>
        <Typography textAlign="center" variant="h2">רכישות</Typography>
        <Stack direction="row" alignItems="center" gap={2}
          justifyContent="space-between">
          <SCButton text="הוספת רכישה שלא בוצעה בסליקה"
            onClick={addPurchase} variant="outlined" icon={<AddIcon/>}/>
          {!!total&&<Typography fontWeight="bold">
            סה"כ: {total}₪
          </Typography>}
        </Stack>
        <TableContainer component={Card}>
          <Table sx={{minWidth: 650}}>
            <TableHead sx={{backgroundColor: 'rgba(0, 0, 0, 0.05)'}}>
              <TableRow>
                <TableCell align="right">
                  <Typography>תיאור</Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography>סכום</Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography>שולם ב</Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography>שם משתמש</Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography>דוא"ל</Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography>פעולות</Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {purchases?.map(p=><TableRow key={p.id}>
                <TableCell align="right">{p.description}</TableCell>
                <TableCell align="right">{p.amount}₪</TableCell>
                <TableCell align="right">{formatTime(p.completed)}</TableCell>
                <TableCell align="right">{p.username}</TableCell>
                <TableCell align="right">{p.email}</TableCell>
                <TableCell align="right">
                  <Stack direction="row" gap={1} alignItems="center">
                    <Tooltip title={p.note||'הערות'}>
                      <IconButton size="small" onClick={()=>editNote(p)}>
                        <NoteIcon/>
                      </IconButton>
                    </Tooltip>
                    {p.manual&&<>
                      <Tooltip title="עריכה">
                        <IconButton size="small" onClick={()=>updatePurchase(p)}>
                          <EditIcon/>
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="מחיקה">
                        <IconButton size="small" onClick={()=>deletePurchase(p)}
                          color="error">
                          <DeleteIcon/>
                        </IconButton>
                      </Tooltip>
                    </>}
                  </Stack>
                </TableCell>
              </TableRow>)}
            </TableBody>
          </Table>
        </TableContainer>
      </Stack>
    </Container>;
};

export default withMainContainer(AdminPurchases);
